import {
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  Button,
  Space,
  Table,
  Tooltip,
  Input,
  DatePicker,
  Badge,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { SearchOutlined } from '@ant-design/icons';
import usePaymentsList from "../../hooks/usePaymentsList";
import useMemberProfilesList from "../../hooks/useMemberProfilesList";
import Highlighter from 'react-highlight-words';
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from 'moment';

const { RangePicker } = DatePicker;

function StandingOrderDataTable() {
  const [data, setData] = useState([]);
  const [sdata, setSData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);

  const paymentList = usePaymentsList("");
  const memberProfileList = useMemberProfilesList("");

  useEffect(() => {
    const tempPaymentListData = []
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    paymentList?.map((payment_list_data, indexNo) => {
      memberProfileList?.map((member_profile_list_data) => {
        if (payment_list_data.member_id === member_profile_list_data.member_id && payment_list_data.payment_type === "Standing Order") {
          let isExpiringSoon = false;

          // Determine last payment date string (handles string with commas or arrays)
          const rawDates = payment_list_data.payment_date;
          let lastDateStr = "";
          if (Array.isArray(rawDates) && rawDates.length > 0) {
            lastDateStr = String(rawDates[rawDates.length - 1]).trim();
          } else if (typeof rawDates === 'string') {
            const parts = rawDates.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length > 0) lastDateStr = parts[parts.length - 1];
          }

          if (lastDateStr) {
            const [lastYear, lastMonth] = lastDateStr.split('-').map(Number);
            
            // If the year is less than current year OR (year is same but month is less than current)
            // It is EXPIRED.
            const isExpired = lastYear < currentYear || (lastYear === currentYear && lastMonth < currentMonth);
            
            if (isExpired) {
                return null; // Skip adding this record
            }
            
            // Show red dot if last payment date is in current month and year
            if (lastYear === currentYear && lastMonth === currentMonth) {
              isExpiringSoon = true;
            }
          }

          tempPaymentListData.push({
            "key": indexNo,
            "_id": payment_list_data._id,
            "payment_number": payment_list_data.payment_number,
            "payment_date": `${payment_list_data.payment_date} `,
            "member_id": payment_list_data.member_id,
            "member_name": `${member_profile_list_data.first_name} ${member_profile_list_data.last_name}`,
            "hash_name": member_profile_list_data.hash_name,
            "amount": payment_list_data.amount?.toFixed(2),
            "payment_description": payment_list_data.payment_description,
            "invoice": payment_list_data.invoice,
            "isExpiringSoon": isExpiringSoon
          })
        }

        return null
      })

      return null
    })

    setData(tempPaymentListData);
    setSData(tempPaymentListData);
  }, [memberProfileList, paymentList])

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const exportPaymentListDataPDF = (record) => {
    
  const unit = "pt";
  const size = "A4";
  const orientation = "portrait";
  const marginLeft = 40;
  const doc = new jsPDF(orientation, unit, size);
  
  // Title & Subtitle
  doc.setFontSize(15);
  doc.text("Colombo Hash", marginLeft, 30);
  doc.setFontSize(12);
  doc.text(`Payment - ${record.payment_number || "-"}`, marginLeft, 50);

  // Prepare the data as an array of arrays for the table
  const tableRows = [
    ["Payment Number", record.payment_number || "-"],
    ["Payment Date", record.payment_date || "-"],
    ["Member ID", record.member_id || "-"],
    ["Member Name", record.member_name || "-"],
    ["Hash Name", record.hash_name || "-"],
    ["Amount (LKR)", record.amount || "-"],
    ["Type", "Standing Order"],
    ["Payment Description", record.payment_description || "-"] 
  ];

  // Draw the table
  doc.autoTable({
    startY: 70,
    body: tableRows,
    showHead: false, // Hides the top header row
    theme: 'grid',
    margin: { left: marginLeft },
    columnStyles: {
      0: { 
        fillColor: [200, 230, 255], // Light Blue
        fontStyle: 'bold', 
        textColor: [0, 0, 0],
        cellWidth: 150 
      },
      1: { 
        fillColor: [255, 255, 255] // White
      }
    },
    didParseCell: (data) => {
      // Apply yellow to the last row (Payment Description in this case)
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fillColor = [255, 250, 205]; // Light Yellow
      }
    }
  });

  // Generated date/time bottom-right
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const now = new Date();
  const generatedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
  
  doc.setFontSize(10);
  doc.text(generatedDateTime, pageWidth - marginLeft, pageHeight - 30, { align: "right" });

  doc.save(`payment-number${record.payment_number || 'unknown'}-payment.pdf`);
}

  const handleDownloadInvoice = (record) => {
    // Get the invoice URL from the record object
    const invoiceUrl = record.invoice;
    window.open(invoiceUrl, '_blank');
  };

  const handleRangePickerChange = (value, dateString) => {
    if (dateString[0] === "" && dateString[1] === "") {
      setData(sdata);
    } else {
      // Convert the dateString array to actual Date objects for comparison
      const startDate = new Date(dateString[0]);
      const endDate = new Date(dateString[dateString.length - 1]);

      // Filter the data array based on payment_date within the range
      const filteredData = data.filter(item => {
        // Split payment_date to handle multiple dates if present
        const paymentDates = item.payment_date.split(',').map(date => date.trim());
        for (const date of paymentDates) {
          const paymentDate = new Date(date);
          if (paymentDate >= startDate && paymentDate <= endDate) {
            return true; // Include the item if payment_date is within the range
          }
        }
        return false; // Exclude the item if payment_date is not within the range
      });

      setData(filteredData);
    }
  }

  const columns = [
    {
      title: "Payment Number",
      dataIndex: "payment_number",
      sorter: (a, b) => a.payment_number - b.payment_number,
      sortOrder: 'descend',
      ...getColumnSearchProps('payment_number'),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      ...getColumnSearchProps('payment_date'),
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      ...getColumnSearchProps('member_id'),
       render: (text, record) => (
        record.isExpiringSoon ? (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Badge color="red" style={{ marginRight: 8 }} />{text}
          </span>
        ) : (
          text
        )
      ),
    },
    {
      title: "Member Name",
      dataIndex: "member_name",
      ...getColumnSearchProps('member_name'),
    },
    {
      title: "Hash Name",
      dataIndex: "hash_name",
      ...getColumnSearchProps('hash_name'),
    },
    {
      title: "Amount (LKR)",
      dataIndex: "amount",
    },
    {
      title: "Payment Description",
      dataIndex: "payment_description",
      ...getColumnSearchProps('payment_description'),
    },
    {
      title: "Action",
      render: (text, record) => (
        <Space>
          <Tooltip placement="bottom" title="Download Invoice">
            <Button
              className="edit_button"
              shape="circle"
              icon={<FileTextOutlined />}
              onClick={() => exportPaymentListDataPDF(record)}
            />
          </Tooltip>
          {record.invoice ? <Tooltip placement="bottom" title="Download Standing Order Invoice">
            <Button
              className="edit_button"
              shape="circle"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadInvoice(record)}
            />
          </Tooltip> : <></>}
        </Space>
      ),
      fixed: "right",
      align: 'left',
    },
  ];

  return (
    <>
      <div className="top_second_row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <RangePicker onChange={handleRangePickerChange} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Badge color="red" />
          <span>Expiring Soon</span>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        bordered
        pagination
        scroll={{ x: 1500 }}
      />
    </>
  );
}

export default StandingOrderDataTable;
